import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, deleteDoc, doc, getDoc, getDocs, serverTimestamp, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged, sendPasswordResetEmail, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth, db } from '../firebaseConfig';
import '../App.css';
import './AdminPage.css';

const statusLabel = (item) => {
  if (item.publicationStatus) return item.publicationStatus;
  if (item.moderation?.status === 'spam') return 'spam';
  return 'awaiting-board';
};

const formatDate = (item) => {
  if (item.createdAt?.toDate) return item.createdAt.toDate().toLocaleString();
  return item.timestamp || 'Date unavailable';
};

function AdminPage() {
  const [authReady, setAuthReady] = useState(false);
  const [user, setUser] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    document.title = 'Board Follow-up Admin | Hidden Creek';
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.name = 'robots';
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.content = 'noindex, nofollow, noarchive, nosnippet';
  }, []);

  const loadContent = useCallback(async () => {
    setLoading(true);
    setNotice('');
    try {
      const snapshot = await getDocs(collection(db, 'submissions'));
      const loaded = snapshot.docs
        .map((entry) => ({ id: entry.id, ...entry.data() }))
        .sort((a, b) => {
          const aDate = a.createdAt?.toMillis?.() || Date.parse(a.timestamp || '') || 0;
          const bDate = b.createdAt?.toMillis?.() || Date.parse(b.timestamp || '') || 0;
          return bDate - aDate;
        });
      setSubmissions(loaded);
    } catch (error) {
      console.error('Unable to load submissions:', error);
      setNotice('Submissions could not be loaded. Confirm that this account has administrator access.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => onAuthStateChanged(auth, async (nextUser) => {
    setAuthReady(false);
    setAuthorized(false);
    setUser(nextUser);
    if (!nextUser) {
      setAuthReady(true);
      return;
    }
    try {
      const adminRecord = await getDoc(doc(db, 'admins', nextUser.uid));
      setAuthorized(adminRecord.exists());
    } catch (error) {
      console.error('Unable to verify administrator access:', error);
    } finally {
      setAuthReady(true);
    }
  }), []);

  useEffect(() => {
    if (authorized) loadContent();
  }, [authorized, loadContent]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginError('');
    setResetMessage('');
    setIsSigningIn(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      setPassword('');
    } catch (error) {
      console.error('Administrator sign-in failed:', error);
      setLoginError('The email or password is incorrect.');
    } finally {
      setIsSigningIn(false);
    }
  };

  const handlePasswordReset = async () => {
    setLoginError('');
    setResetMessage('');

    if (!email.trim()) {
      setLoginError('Enter your email address first.');
      return;
    }

    setIsResetting(true);
    try {
      await sendPasswordResetEmail(auth, email.trim());
    } catch (error) {
      console.error('Password reset request failed:', error);
    } finally {
      setResetMessage('If an account exists for that address, a password reset email has been sent.');
      setIsResetting(false);
    }
  };

  const changeStatus = async (item, publicationStatus) => {
    setNotice('');
    const updates = { publicationStatus };
    if (publicationStatus === 'published') updates.boardReviewedAt = serverTimestamp();

    try {
      await updateDoc(doc(db, 'submissions', item.id), updates);
      setSubmissions((current) => current.map((entry) => (
        entry.id === item.id ? { ...entry, publicationStatus } : entry
      )));
      setNotice(publicationStatus === 'published'
        ? 'Submission published to the public board follow-up page.'
        : 'Submission hidden from public view.');
    } catch (error) {
      console.error('Unable to update submission:', error);
      setNotice('That change could not be saved.');
    }
  };

  const removeSubmission = async (item) => {
    if (!window.confirm('Permanently delete this submission? This cannot be undone.')) return;
    setNotice('');
    try {
      await deleteDoc(doc(db, 'submissions', item.id));
      setSubmissions((current) => current.filter((entry) => entry.id !== item.id));
      setNotice('Submission permanently deleted.');
    } catch (error) {
      console.error('Unable to delete submission:', error);
      setNotice('That submission could not be deleted.');
    }
  };

  const filteredSubmissions = useMemo(() => {
    const term = search.trim().toLowerCase();
    return submissions.filter((item) => {
      const matchesStatus = statusFilter === 'all' || statusLabel(item) === statusFilter;
      const body = String(item.feedbacktext || '').toLowerCase();
      const type = String(item.feedbackType || '').toLowerCase();
      return matchesStatus && (!term || body.includes(term) || type.includes(term));
    });
  }, [search, statusFilter, submissions]);

  const counts = useMemo(() => ({
    total: submissions.length,
    awaiting: submissions.filter((item) => statusLabel(item) === 'awaiting-board').length,
    published: submissions.filter((item) => statusLabel(item) === 'published').length,
    flagged: submissions.filter((item) => ['hidden', 'spam'].includes(statusLabel(item))).length,
  }), [submissions]);

  if (!authReady) return <div className="admin-loading">Preparing the moderation desk…</div>;

  if (!user) {
    return (
      <main className="admin-login-shell">
        <section className="admin-login-copy">
          <a className="brand admin-brand" href="/">
            <span className="brand-mark" aria-hidden="true">HC</span>
            <span><strong>Hidden Creek</strong><small>Board follow-up admin</small></span>
          </a>
          <p className="eyebrow">Private workspace</p>
          <h1>From resident voice to public follow-up.</h1>
          <p>Review submissions, record when topics reach the board, and control what becomes part of the public community record.</p>
        </section>
        <section className="admin-login-card" aria-labelledby="admin-login-title">
          <p className="card-kicker">Authorized access only</p>
          <h2 id="admin-login-title">Sign in to continue.</h2>
          <form onSubmit={handleLogin}>
            <label htmlFor="admin-email">Email address</label>
            <input id="admin-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="username" required />
            <label htmlFor="admin-password">Password</label>
            <div className="admin-password-field">
              <input id="admin-password" type={showPassword ? 'text' : 'password'} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required />
              <button
                type="button"
                className="admin-password-toggle"
                onClick={() => setShowPassword((visible) => !visible)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            <button type="button" className="admin-forgot-password" onClick={handlePasswordReset} disabled={isResetting}>
              {isResetting ? 'Sending reset email…' : 'Forgot password?'}
            </button>
            {loginError && <p className="admin-error" role="alert">{loginError}</p>}
            {resetMessage && <p className="admin-reset-message" role="status">{resetMessage}</p>}
            <button type="submit" className="admin-primary" disabled={isSigningIn}>
              {isSigningIn ? 'Signing in…' : 'Sign in'} <span aria-hidden="true">→</span>
            </button>
          </form>
          <a className="admin-back-link" href="/">← Return to the community site</a>
        </section>
      </main>
    );
  }

  if (!authorized) {
    return (
      <main className="admin-access-shell">
        <div className="admin-access-card">
          <p className="card-kicker">Access not configured</p>
          <h1>This account is signed in, but it is not an administrator.</h1>
          <p>Add the following UID as a document in the Firestore <code>admins</code> collection:</p>
          <code className="admin-uid">{user.uid}</code>
          <button className="admin-secondary" type="button" onClick={() => signOut(auth)}>Sign out</button>
        </div>
      </main>
    );
  }

  return (
    <div className="admin-shell">
      <header className="admin-header">
        <a className="brand" href="/"><span className="brand-mark" aria-hidden="true">HC</span><span><strong>Hidden Creek</strong><small>Board follow-up admin</small></span></a>
        <div className="admin-account"><span>{user.email}</span><button type="button" onClick={() => signOut(auth)}>Sign out</button></div>
      </header>
      <main className="admin-main">
        <section className="admin-welcome">
          <div>
            <p className="eyebrow">Submission review</p>
            <h1>Track what reaches the board.</h1>
            <p>Submissions stay private until you confirm that the topic has been brought before the board and publish it for community reference.</p>
          </div>
          <button className="admin-refresh" type="button" onClick={loadContent} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh submissions'}</button>
        </section>
        <section className="admin-stats" aria-label="Submission summary">
          <article><span>Total submissions</span><strong>{counts.total}</strong></article>
          <article><span>Awaiting board</span><strong>{counts.awaiting}</strong></article>
          <article><span>Public follow-ups</span><strong>{counts.published}</strong></article>
          <article><span>Hidden or spam</span><strong>{counts.flagged}</strong></article>
        </section>
        <section className="admin-content">
          <div className="admin-toolbar">
            <div className="admin-section-title"><strong>Resident submissions</strong><span>{submissions.length}</span></div>
            <div className="admin-filters">
              <label><span className="sr-only">Search submissions</span><input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search messages…" /></label>
              <label><span className="sr-only">Filter by status</span><select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value="all">All statuses</option><option value="awaiting-board">Awaiting board</option><option value="published">Published after board</option><option value="hidden">Hidden</option><option value="spam">Spam</option>
              </select></label>
            </div>
          </div>
          {notice && <p className="admin-notice" role="status">{notice}</p>}
          <div className="moderation-list">
            {!loading && filteredSubmissions.length === 0 && <div className="admin-empty"><span aria-hidden="true">✓</span><h2>Nothing to review here.</h2><p>Try another status or search term.</p></div>}
            {filteredSubmissions.map((item) => {
              const status = statusLabel(item);
              return <article className="moderation-card" key={item.id}>
                <div className="moderation-meta"><span className={`status-pill status-${status}`}>{status.replace('-', ' ')}</span><span>{item.feedbackType || 'Anonymous submission'}</span><time>{formatDate(item)}</time></div>
                <p className="moderation-message">{item.feedbacktext || 'No message content.'}</p>
                {item.moderation?.reasons?.length > 0 && <p className="moderation-reasons">Flagged for: {item.moderation.reasons.join(', ')}</p>}
                <div className="moderation-actions">
                  {status !== 'published' && <button type="button" className="action-publish" onClick={() => changeStatus(item, 'published')}>Board addressed — publish</button>}
                  {status !== 'hidden' && <button type="button" onClick={() => changeStatus(item, 'hidden')}>Hide</button>}
                  <button type="button" className="action-delete" onClick={() => removeSubmission(item)}>Delete permanently</button>
                </div>
              </article>;
            })}
          </div>
        </section>
      </main>
    </div>
  );
}

export default AdminPage;
